'use client';
// Mini price chart for one strategy at a time. The expanded HedgeLab card
// drives which strategy is shown — strikes overlay reflects only that
// strategy's legs (so the chart actually maps to the trade you're looking at,
// not a confusing average across all strategies). Trim has no option legs;
// the chart still renders with just the spot line so the panel layout
// doesn't reflow when you click between strategies.

import { useMemo } from 'react';
import { LineChart, Line, XAxis, YAxis, ReferenceLine, ReferenceArea, ResponsiveContainer, Tooltip } from 'recharts';
import { useResource } from '@/lib/use-resource';
import type { HedgeStrategy, HedgeLeg } from '@/lib/hedge-structures';
import { resamplePrices, type Period } from '@/lib/price-resample';

/**
 * Resolve the strategy's breakeven price. Prefer the explicit `breakevenPerShare`
 * field on the strategy; otherwise interpolate from the scenarios array (find
 * the segment where P&L flips sign and linear-interp to zero).
 */
function findBreakeven(strategy: HedgeStrategy | null): number | null {
  if (!strategy) return null;
  if (strategy.type === 'trim') return strategy.spot;             // breakeven on retained 75% = entry
  if (strategy.type === 'collar') {
    // collar net = put_cost − call_credit; breakeven on the long-stock leg = spot + net
    return strategy.spot + strategy.netUpfrontPerShare;
  }
  if (typeof strategy.breakevenPerShare === 'number' && Number.isFinite(strategy.breakevenPerShare)) {
    return strategy.breakevenPerShare;
  }
  // Fall back to interpolation across scenarios
  const sc = strategy.scenarios ?? [];
  for (let i = 0; i < sc.length - 1; i++) {
    const a = sc[i];
    const b = sc[i + 1];
    if (a.pnlPerShare === 0) return a.priceAtExpiry;
    if (b.pnlPerShare === 0) return b.priceAtExpiry;
    if (Math.sign(a.pnlPerShare) !== Math.sign(b.pnlPerShare)) {
      const slope = (b.pnlPerShare - a.pnlPerShare) / (b.priceAtExpiry - a.priceAtExpiry);
      return a.priceAtExpiry - a.pnlPerShare / slope;
    }
  }
  return null;
}

interface PricePoint { date: string; close: number }

interface StrikeOverlay {
  strike: number;
  label: string;       // e.g., "Sell $720 C"
  color: string;       // tailwind hex
}

function overlaysFromStrategy(strategy: HedgeStrategy | null): StrikeOverlay[] {
  if (!strategy) return [];
  const out: StrikeOverlay[] = [];
  for (const leg of strategy.legs) {
    if (leg.asset === 'stock' || leg.strike == null) continue;
    out.push(strikeOverlayForLeg(leg));
  }
  return out;
}

function strikeOverlayForLeg(leg: HedgeLeg): StrikeOverlay {
  const isCall = leg.asset === 'call';
  const verb = leg.action === 'sell' ? 'Sell' : 'Buy';
  const sideLetter = isCall ? 'C' : 'P';
  // Color reflects WHICH SIDE of the position you're hedging:
  //   red   = call leg (you cap upside)
  //   green = put leg (you protect downside)
  const color = isCall ? '#ef4444' : '#10b981';
  return {
    strike: leg.strike!,
    label: `${verb} $${leg.strike} ${sideLetter}`,
    color,
  };
}

export function HedgeChart({
  symbol,
  strategy,
  daysBack = 90,
  period = 'D',
  height = 260,
}: {
  symbol: string;
  /** The currently-expanded strategy. Null/undefined → just the price + spot. */
  strategy: HedgeStrategy | null;
  daysBack?: number;
  /** Period selector — D (daily, default), W (weekly), M (monthly). When W or M
   *  the daily series is resampled to last-close-per-period bars and the lookback
   *  window stretches accordingly so the chart still has ~60-90 bars. */
  period?: Period;
  height?: number;
}) {
  const { data } = useResource<{ prices?: PricePoint[] }>(`/api/prices?symbol=${symbol}`);
  const series = useMemo(() => {
    const all = data?.prices ?? [];
    if (period === 'D') return all.slice(-daysBack).map((p) => ({ date: p.date, close: p.close }));
    // Weekly: ~3 years of weekly bars (≈156). Monthly: ~10 years (≈120).
    const lookbackDays = period === 'W' ? 365 * 3 : 365 * 10;
    const trimmed = all.slice(-lookbackDays);
    return resamplePrices(trimmed, period);
  }, [data, daysBack, period]);

  if (series.length === 0) {
    return <div className="text-[10px] text-[#64748b] text-center py-6">loading price history…</div>;
  }

  const overlays = overlaysFromStrategy(strategy);
  const spot = strategy?.spot;
  const breakeven = findBreakeven(strategy);

  const closes = series.map(p => p.close);
  const allStrikes = overlays.map(o => o.strike);
  const yMin = Math.min(...closes, ...(allStrikes.length ? allStrikes : [Infinity]), spot ?? Infinity, breakeven ?? Infinity) * 0.94;
  const yMax = Math.max(...closes, ...(allStrikes.length ? allStrikes : [-Infinity]), spot ?? -Infinity, breakeven ?? -Infinity) * 1.06;

  // Right margin is bumped to fit "Sell $720 C" labels without clipping.
  const periodLabel = period === 'D' ? '90-day' : period === 'W' ? '3-year weekly' : '10-year monthly';
  return (
    <div className="space-y-2">
      <div className="text-[10px] uppercase tracking-wider text-[#64748b]">
        {strategy
          ? `${symbol} ${periodLabel} price · strikes from ${strategy.name}`
          : `${symbol} ${periodLabel} price · click a strategy to overlay strikes`}
      </div>
      <div style={{ height }}>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={series} margin={{ top: 10, right: 130, bottom: 0, left: 0 }}>
            <XAxis dataKey="date" stroke="#475569" tick={{ fill: '#64748b', fontSize: 9 }} interval={Math.max(0, Math.floor(series.length / 6))} />
            <YAxis stroke="#475569" tick={{ fill: '#64748b', fontSize: 9 }} domain={[yMin, yMax]} tickFormatter={(v: number) => `$${v.toFixed(0)}`} />
            <Tooltip
              formatter={(v) => [`$${Number(v).toFixed(2)}`, 'Close']}
              contentStyle={{ backgroundColor: '#0a0e14', border: '1px solid #334155', borderRadius: 6, fontSize: 11, color: '#e2e8f0' }}
              labelStyle={{ color: '#94a3b8' }}
            />
            {/* P&L shading — above breakeven the strategy makes money,
                below it loses. Drawn FIRST so the price line + strike
                lines render on top. */}
            {breakeven != null && (
              <>
                <ReferenceArea y1={breakeven} y2={yMax} fill="#10b981" fillOpacity={0.10} stroke="none" ifOverflow="visible" />
                <ReferenceArea y1={yMin} y2={breakeven} fill="#ef4444" fillOpacity={0.10} stroke="none" ifOverflow="visible" />
              </>
            )}
            <Line type="monotone" dataKey="close" stroke="#e2e8f0" strokeWidth={1.6} dot={false} />
            {spot != null && (
              <ReferenceLine
                y={spot}
                stroke="#c9a84c"
                strokeDasharray="2 2"
                label={{ value: `spot $${spot.toFixed(2)}`, fill: '#c9a84c', fontSize: 10, position: 'right' }}
              />
            )}
            {/* Hide the breakeven LINE+label when it's within $1 of spot
                (e.g., Trim, where breakeven IS spot). The label would otherwise
                overlap the spot label and turn into illegible overplotted text.
                The green/red shading still pivots on breakeven so the visual
                "above is profit / below is loss" cue is preserved. */}
            {breakeven != null && spot != null && Math.abs(breakeven - spot) > 1 && (
              <ReferenceLine
                y={breakeven}
                stroke="#fbbf24"
                strokeDasharray="4 2"
                label={{ value: `breakeven $${breakeven.toFixed(2)}`, fill: '#fbbf24', fontSize: 10, position: 'right' }}
              />
            )}
            {overlays.map((o, i) => (
              <ReferenceLine
                key={`${o.strike}-${i}`}
                y={o.strike}
                stroke={o.color}
                strokeDasharray="3 3"
                label={{ value: o.label, fill: o.color, fontSize: 10, position: 'right' }}
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </div>
      {strategy && (
        <div className="flex flex-wrap gap-x-3 gap-y-1 text-[10px] text-[#94a3b8]">
          <span className="inline-flex items-center gap-1.5">
            <span className="inline-block w-3 h-2.5" style={{ backgroundColor: '#10b981', opacity: 0.4 }} />
            <span>profit zone (above breakeven)</span>
          </span>
          <span className="inline-flex items-center gap-1.5">
            <span className="inline-block w-3 h-2.5" style={{ backgroundColor: '#ef4444', opacity: 0.4 }} />
            <span>loss zone (below breakeven)</span>
          </span>
          {breakeven != null && (spot == null || Math.abs(breakeven - spot) > 1) && (
            <span className="inline-flex items-center gap-1.5">
              <span className="inline-block w-3 h-px" style={{ backgroundColor: '#fbbf24' }} />
              <span>breakeven ${breakeven.toFixed(2)}</span>
            </span>
          )}
          {breakeven != null && spot != null && Math.abs(breakeven - spot) <= 1 && (
            <span className="inline-flex items-center gap-1.5 text-[#fbbf24]/80 italic">
              <span>breakeven ≈ spot</span>
            </span>
          )}
          {spot != null && (
            <span className="inline-flex items-center gap-1.5">
              <span className="inline-block w-3 h-px border-t border-dashed" style={{ borderColor: '#c9a84c' }} />
              <span>spot ${spot.toFixed(2)}</span>
            </span>
          )}
          {overlays.map((o, i) => (
            <span key={i} className="inline-flex items-center gap-1.5">
              <span className="inline-block w-3 h-px border-t border-dashed" style={{ borderColor: o.color }} />
              <span>{o.label}</span>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
